import { DotRezSessionBase } from "../dot-rez-session-base";
import {IDotRezUserSession} from "./dot-rez-user.session.interface";
import { ITripScheduleRequest } from "../../data-contracts/requests/trip-schedule.request";
import { ITripScheduleResponse } from "../../data-contracts/responses/trip-schedule.response";
import { IServiceFactory } from "../../../service-factory.interface";
import { VoidDotRezSessionTimer } from "../session-timer/void-dot-rez-session-timer";
import {IDotRezCurrencyConversionRequest} from "../../data-contracts/requests/currency-conversion.request";
import {ICurrencyConversionResponse} from "../../data-contracts/responses/currency-conversion.response";
import {UserSessionCodesSettings} from "./user-session-codes-settings";
import {
    IDotRezUserSessionCodesSettingsResponse
} from "../../data-contracts/responses/user-session-codes-settings.response";
import {UserSessionClassesOfService} from "./user-session-classes-of-service";
import {IDotRezAvailabilityLowFareRequest} from "../../data-contracts/requests/lowfare-search.request";
import {DotRezResponse} from "../dot-rez-response";
import {IImpersonateWithAgentRequest} from "../../data-contracts/requests/impersonate-with-agent.request";
import {DotRezAvailabilityLowFareResponse} from "../../data-contracts/responses/lowfare-search.response";

export class DotRezUserSession extends DotRezSessionBase implements IDotRezUserSession {
    constructor(token: string,
                services: IServiceFactory) {
        super(token, services, new VoidDotRezSessionTimer())
    }

    async tripSchedule(request: ITripScheduleRequest): Promise<DotRezResponse<ITripScheduleResponse>> {
        return await this.get<ITripScheduleResponse>(`/api/nsk/v1/trip/schedule?Origin=${request.origin}&Destination=${request.destination}&BeginDate=${request.beginDate}&EndDate=${request.endDate}`, {
            doNotThrowOnError: true,
        });
    }

    async availabilityLowFare(request: IDotRezAvailabilityLowFareRequest): Promise<DotRezAvailabilityLowFareResponse> {
        if((request.classesOfService?.length ?? 0) === 0) {
            return await this.getLowFareSimple(request);
        } else {
            return await this.getLowFareAdvanced(request);
        }
    }

    private async getLowFareSimple(request: IDotRezAvailabilityLowFareRequest): Promise<DotRezAvailabilityLowFareResponse> {
        const payload: any = {
            origin: request.origin,
            destination: request.destination,
            beginDate: request.middleDate,
            daysToLeft: request.daysToLeft,
            daysToRight: request.daysToRight,
            passengers: request.passengers,
            currencyCode: request.currencyCode,
            loyaltyFilter: request.loyaltyFilter
        }

        if(request.promotionCode) {
            payload.promotionCode = request.promotionCode;
        }

        const apiUrl = '/api/nsk/v3/availability/lowfare/simple';


        const response = await this.post<DotRezAvailabilityLowFareResponse>(apiUrl, {
            body: payload
        });
        return response.data;

    }

    private async getLowFareAdvanced(request: IDotRezAvailabilityLowFareRequest): Promise<DotRezAvailabilityLowFareResponse> {

        const payload: any = {
            bypassCache: false,
            getAllDetails: false,
            includeTaxesAndFees: false,
            passengers:{
                types: request.passengers
            },
            codes: {
                currency: request.currencyCode
            },
            filters: {
                bookingClasses: request.classesOfService,
                loyalty: request.loyaltyFilter,

            },
            criteria: [
                {
                    originStationCodes: [request.origin],
                    destinationStationCodes: [request.destination],
                    beginDate: request.beginDate,
                    endDate: request.endDate
                }
            ]
        }

        const apiUrl = '/api/nsk/v3/availability/lowfare';

        const response = await this.post<DotRezAvailabilityLowFareResponse>(apiUrl, {
            body: payload
        });
        return response.data;
    }

    async convertCurrency(request: IDotRezCurrencyConversionRequest): Promise<ICurrencyConversionResponse> {
        const response = await this.get<ICurrencyConversionResponse>(`/api/nsk/v1/currency/converter?FromCurrencyCode=${request.fromCurrencyCode}&ToCurrencyCode=${request.toCurrencyCode}&Amount=${request.amount}`);
        return response.data;
    }

    private _codesSettingsPromise:  Promise<UserSessionCodesSettings> | null = null;
    private async getCodesSettings(): Promise<UserSessionCodesSettings> {
        if(!this._codesSettingsPromise) {

            this._codesSettingsPromise = this.get<IDotRezUserSessionCodesSettingsResponse>('/api/nsk/v1/settings/general/codes')
                .then(response => new UserSessionCodesSettings(response.data))
                .catch((err) => {
                    this.services.logger.error('Failed to get codes settings', err);
                    this._codesSettingsPromise = null;
                    throw err;
                });
        }
        return this._codesSettingsPromise;
    }

    async getClassesOfService(): Promise<UserSessionClassesOfService> {
        return (await this.getCodesSettings()).classesOfService;
    }

    async impersonateWithAgent(request: IImpersonateWithAgentRequest): Promise<void> {
        this._codesSettingsPromise = null;
        const apiResponse = await this.services.airlineWebapi.impersonateUser({
            dotRezToken: this.token,
            agentName: request.agent
        });

        if(apiResponse.token) {
            this.token = apiResponse.token;
        }
    }

    async impersonateToAnonymous(): Promise<void> {
        this._codesSettingsPromise = null;
        const apiResponse = await this.services.airlineWebapi.impersonateToAnonymous({
            dotRezToken: this.token
        });

        if(apiResponse.token) {
            this.token = apiResponse.token;
        }
    }


}
